import { useState, useEffect } from "react"
import { socket } from "@/utils/utils"
import { mapDesigns } from "@mono/mapDesigns"
import { featureFlags } from "@mono/constants"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { useBackHandler } from "@/Hooks/useBackHandler"
import {
  SAFE_OVERLAY_PADDING,
  SAFE_OVERLAY_PADDING_SM,
} from "@/utils/safeArea"

interface SinglePlayerModalProps {
  isOpen: boolean
  onClose: () => void
}

const playerOptions = [{ count: 4, bots: 3 }]

const SinglePlayerModal: React.FC<SinglePlayerModalProps> = ({ isOpen, onClose }) => {
  const [isStarting, setIsStarting] = useState(false)
  const [selectedMapId, setSelectedMapId] = useState("1")
  const [selectedPlayerCount, setSelectedPlayerCount] = useState(playerOptions[0].count)

  // Android back closes the sheet rather than falling through to the lobby,
  // where it would send the app to the background.
  useBackHandler(() => {
    onClose()
    return true
  }, isOpen)

  const handleStartGame = async (playerCount: number) => {
    if (!isOpen || isStarting) return
    setIsStarting(true)

    socket.emit("lobby.createGame", async (gameId: string) => {
      socket.emit("lobby.hostChangeGameSettings", {
        id: gameId,
        mapId: selectedMapId,
        maxTurns: 35,
      })

      // Add bots one by one
      const botsToAdd = playerCount - 1
      for (let i = 0; i < botsToAdd; i++) {
        await new Promise((resolve) => {
          socket.emit("newgame.add-bot", gameId, (success: boolean) => {
            resolve(success)
          })
        })
      }

      // Start the game in the background. The user stays on the "Game starting…"
      // screen below until the global "newgame.gameStarted" handler (App.tsx)
      // navigates straight to /game/:id — so the /newgame lobby never flashes.
      socket.emit("newgame.startGame", gameId, "")
    })
  }

  if (!isOpen) return null

  // While the game is being created/started, show a polished loading screen
  // instead of the configuration modal (and instead of the /newgame lobby).
  if (isStarting) {
    return <GameStartingScreen />
  }

  return (
    <div className={`fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 ${SAFE_OVERLAY_PADDING} ${SAFE_OVERLAY_PADDING_SM}`}>
      <div className="relative w-full max-w-xl rounded-3xl overflow-hidden shadow-[0_0_80px_rgba(251,191,36,0.12)] border border-amber-400/20 bg-gradient-to-b from-gray-900 via-gray-950 to-black sm:max-w-[480px] sm:max-h-[94vh] sm:overflow-y-auto sm:rounded-[18px]">
        {/* Decorative top accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent rounded-full" />

        {/* Close button */}
        <div className="absolute top-4 right-4 z-10 sm:top-[8px] sm:right-[8px]">
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Header */}
        <div className="flex flex-col items-center pt-10 pb-6 px-8 sm:pt-[16px] sm:pb-[10px] sm:px-[16px]">
          <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 mb-4 sm:h-[38px] sm:w-[38px] sm:rounded-[12px] sm:mb-[8px]">
            <i className="fas fa-robot text-white text-xl sm:text-[18px]"></i>
          </div>
          <h2 className="font-fourth font-bold text-white text-2xl tracking-wide sm:text-[21px] sm:leading-[26px]">Single Player</h2>
          <p className="text-white/40 text-sm mt-1 sm:text-[12px] sm:leading-[16px] sm:mt-[2px]">Play against bots</p>
        </div>

        {/* Map Selection */}
        <div className="px-8 pb-5 sm:px-[16px] sm:pb-[10px]">
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block sm:text-[11px] sm:leading-[14px] sm:mb-[6px]">Map</label>
          <div className="flex gap-2 sm:gap-[8px]">
            {mapDesigns.map((map) => {
              const isDisabled = map.id === "0" && !featureFlags.allowStandardMapInSinglePlayerMode
              return (
                <div key={map.id} className="flex-1 flex flex-col gap-1">
                  <button
                    onClick={() => !isDisabled && setSelectedMapId(map.id)}
                    disabled={isDisabled}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 text-sm font-semibold sm:px-[12px] sm:py-[10px] sm:rounded-[10px] sm:text-[14px] sm:leading-[18px] ${
                      isDisabled
                        ? "border-white/5 bg-white/[0.02] text-white/20 cursor-not-allowed"
                        : selectedMapId === map.id
                        ? "border-amber-500 bg-amber-500/20 text-white"
                        : "border-white/10 bg-white/[0.03] text-white/50 hover:bg-white/[0.07] hover:border-white/20"
                    }`}
                  >
                    {map.name}
                  </button>
                  {isDisabled && (
                    <span className="text-[10px] text-amber-400/70 text-center sm:text-[10px] sm:leading-[13px]">
                      Available in full version — Wishlist now!
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Player Count Options */}
        <div className="px-8 pb-5 sm:px-[16px] sm:pb-[10px]">
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block sm:text-[11px] sm:leading-[14px] sm:mb-[6px]">Players</label>
          <div className="flex flex-col gap-2 sm:gap-[8px]">
            {playerOptions.map(({ count, bots }) => (
              <button
                key={count}
                onClick={() => setSelectedPlayerCount(count)}
                disabled={isStarting}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl border transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group sm:gap-[10px] sm:px-[12px] sm:py-[10px] sm:rounded-[10px] ${
                  selectedPlayerCount === count
                    ? "border-amber-500 bg-amber-500/20"
                    : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20"
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md shrink-0 sm:h-[32px] sm:w-[32px] sm:rounded-[10px]">
                  <span className="text-white font-bold text-sm sm:text-[14px] sm:leading-[18px]">{count}</span>
                </div>
                <div className="text-left flex-1">
                  <div className="text-white font-semibold text-sm sm:text-[14px] sm:leading-[18px]">{count} Players</div>
                  <div className="text-white/40 text-xs sm:text-[12px] sm:leading-[16px]">
                    You + {bots} Bot{bots > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="flex -space-x-1.5 sm:-space-x-[5px]">
                  <div className="w-6 h-6 rounded-full bg-amber-500/80 border-2 border-gray-950 flex items-center justify-center sm:h-[20px] sm:w-[20px]">
                    <i className="fas fa-user text-[8px] text-white sm:text-[9px]"></i>
                  </div>
                  {Array.from({ length: bots }).map((_, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 rounded-full bg-white/10 border-2 border-gray-950 flex items-center justify-center sm:h-[20px] sm:w-[20px]"
                    >
                      <i className="fas fa-robot text-[8px] text-white/50 sm:text-[9px]"></i>
                    </div>
                  ))}
                </div>
                <i className="fas fa-chevron-right text-white/20 group-hover:text-white/50 transition-colors text-xs sm:text-[12px]"></i>
              </button>
            ))}
          </div>
        </div>

        {/* Play */}
        <div className="px-8 pb-5 sm:px-[16px] sm:pb-[10px]">
          <button
            onClick={() => handleStartGame(selectedPlayerCount)}
            disabled={isStarting}
            className="w-full py-3 rounded-xl text-sm font-semibold bg-amber-600 hover:bg-amber-500 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed sm:py-[11px] sm:rounded-[10px] sm:text-[15px] sm:leading-[19px]"
          >
            {isStarting ? "Starting..." : "Play"}
          </button>
        </div>

        {/* Cancel */}
        <div className="px-8 pb-8 sm:px-[16px] sm:pb-[14px]">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl text-sm font-medium text-white/40 hover:text-white/70 hover:bg-white/5 transition-all duration-200 sm:py-[9px] sm:rounded-[10px] sm:text-[14px] sm:leading-[18px]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

const loadingSteps = [
  { icon: "fa-dice", label: "Setting up the board" },
  { icon: "fa-robot", label: "Waking up the bots" },
  { icon: "fa-flag-checkered", label: "Starting the game" },
]

const GameStartingScreen: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev < loadingSteps.length - 1 ? prev + 1 : prev))
    }, 1100)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-gradient-to-b from-gray-900 via-gray-950 to-black">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-600/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-orange-600/10 blur-[120px]" />

      <div className="relative flex flex-col items-center px-8 text-center">
        {/* Animated emblem */}
        <div className="relative mb-10 flex h-28 w-28 items-center justify-center sm:mb-[16px] sm:h-[72px] sm:w-[72px]">
          {/* Outer pulsing ring */}
          <span className="absolute inset-0 rounded-full border border-amber-400/20" />
          <span className="absolute inset-0 animate-ping rounded-full border border-amber-400/30 [animation-duration:2s]" />
          {/* Spinning gradient ring */}
          <span
            className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 border-r-orange-500 animate-spin"
            style={{ animationDuration: "1.2s" }}
          />
          {/* Core */}
          <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30 sm:h-[44px] sm:w-[44px] sm:rounded-[12px]">
            <i className="fas fa-robot text-2xl text-white sm:text-[20px]"></i>
          </span>
        </div>

        <h2 className="font-fourth text-2xl font-bold tracking-wide text-white sm:text-[21px] sm:leading-[26px]">
          Game Starting
        </h2>
        <p className="mt-2 text-sm text-white/40 sm:mt-[4px] sm:text-[12px] sm:leading-[16px]">Get ready to face the bots…</p>

        {/* Steps */}
        <div className="mt-9 flex w-full max-w-xs flex-col gap-3 sm:mt-[14px] sm:max-w-[300px] sm:gap-[7px]">
          {loadingSteps.map((step, i) => {
            const isDone = i < activeStep
            const isActive = i === activeStep
            return (
              <div
                key={step.label}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-all duration-500 sm:gap-[9px] sm:rounded-[10px] sm:px-[12px] sm:py-[8px] ${
                  isActive
                    ? "border-amber-500/50 bg-amber-500/10"
                    : isDone
                    ? "border-white/5 bg-white/[0.02]"
                    : "border-white/5 bg-white/[0.02] opacity-40"
                }`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs transition-colors duration-300 sm:h-[24px] sm:w-[24px] sm:rounded-[8px] sm:text-[12px] ${
                    isDone
                      ? "bg-emerald-500/20 text-emerald-400"
                      : isActive
                      ? "bg-amber-500/20 text-amber-300"
                      : "bg-white/5 text-white/40"
                  }`}
                >
                  <i className={`fas ${isDone ? "fa-check" : step.icon}`}></i>
                </div>
                <span
                  className={`flex-1 text-left text-sm font-medium transition-colors duration-300 sm:text-[13px] sm:leading-[17px] ${
                    isActive ? "text-white" : isDone ? "text-white/60" : "text-white/40"
                  }`}
                >
                  {step.label}
                </span>
                {isActive && (
                  <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber-400 [animation-delay:-0.3s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber-400 [animation-delay:-0.15s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber-400" />
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default SinglePlayerModal
